import axios from "axios";
import router from "@/router";

export const API_BASE_URL = "https://canalls.uni-hohenheim.de/backend/index.php";


/**
 * REST API calls.
 */

async function getCall(endpoint, responseType = "json") {
    let result = null
    await axios.get(API_BASE_URL + endpoint, {
        responseType: responseType,
        headers: {
            Authorization: `Bearer ${localStorage.getItem('access_token')}`,
        }
    }).then(response => {
        result = response;
    })
        .catch(error => {
            result = error;
        });
    return result;
}

async function postCall(endpoint, data, contentType = "application/json") {
    let result = null;
    await axios.post(API_BASE_URL + endpoint, data, {
        headers: {
            "Content-Type": contentType,
            Authorization: `Bearer ${localStorage.getItem('access_token')}`,
        }
    }).then(response => {
        result = response;
    }).catch(error => {
        result = error;
    })
    return result;
}

async function putCall(endpoint, data) {
    let result = null
    await axios.put(API_BASE_URL + endpoint, data, {
        headers: {
            Authorization: `Bearer ${localStorage.getItem('access_token')}`,
        }
    }).then(response => {
        result = response;
    }).catch(error => {
        result = error;
    });
    return result;
}

async function deleteCall(endpoint) {
    let result = null
    await axios.delete(API_BASE_URL + endpoint, {
        headers: {
            Authorization: `Bearer ${localStorage.getItem('access_token')}`,
        }
    }).then(response => {
        result = response;
    }).catch(error => {
        result = error;
    });
    return result;
}

/**
 * Backend API Communication.
 */

export async function getBackendVersion() {
    return await getCall('/version');
}

export async function getToC() {
    return await getCall('/toc');
}

export async function postLogin(email, password) {
    return await postCall("/login", {
        email: email,
        password: password
    });
}

export async function postRegistration(data) {
    return await postCall("/register", data)
}

export async function postActivation(email, code) {
    return await postCall('/activate', {
        email: email,
        code: code,
    });
}

export async function postReset(email) {
    return await postCall("/resetPassword", {
        email: email
    });
}

export async function postLogout() {
    return await postCall("/logout", {});
}

export async function getAllFarms() {
    return await getCall('/farms');
}

export async function getUnassignedFarms() {
    return await getCall('/farms/unassigned');
}

export async function getAllRoles() {
    return await getCall('/roles');
}

export async function getAllUsers() {
    return await getCall('/users');
}

export async function getFarmsForUser(userId) {
    return await getCall(`/users/${userId}/farms`);
}

export async function getAllFarmVisits(userId, farms, startDate, endDate) {
    return await getCall(`/visits/farms/${userId}/${farms}/${startDate}/${endDate}`);
}

export async function getVisitPicture(visitId) {
    return await getCall(`/visits/farms/${visitId}/images`, 'blob');
}

export async function getAllGroupVisits(userId, startDate, endDate) {
    return await getCall(`/visits/groups/${userId}/${startDate}/${endDate}`);
}

export async function getGroupVisitPicture(visitId) {
    return await getCall(`/visits/groups/${visitId}/images`, 'blob');
}

export async function getAllTrainingResources() {
    return await getCall(`/resources`);
}

export async function putTrainingResources(data) {
    return await putCall(`/resources`, data);
}

export async function getUserData(userId) {
    return await getCall(`/users/${userId}`);
}

export async function getUserImage(userId) {
    return await getCall(`/users/${userId}/images`, 'blob');
}

export async function postUserImage(userId, imageData) {
    return await postCall(`/users/${userId}/images`, imageData, "multipart/form-data");
}

export async function postUserData(data) {
    return await postCall(`/users`, data);
}

export async function putUserData(userId, userData) {
    return await putCall(`/users/${userId}`, userData);
}

export async function deleteUserData(userId) {
    return await deleteCall(`/users/${userId}`);
}

export async function getAllCountries() {
    return await getCall('/countries');
}

export async function getAllSettings() {
    return await getCall(`/settings`);
}

export async function putSettings(data) {
    return await putCall(`/settings`, data);
}

export async function getAllDiagnosticsDimensions() {
    return await getCall('/diagnostics/dimensions');
}

export async function getAllDiagnosticsPrinciples() {
    return await getCall('/diagnostics/principles');
}

export async function getAllDiagnosticsIndicators() {
    return await getCall('/diagnostics/indicators');
}

export async function getAllDiagnosticsValues() {
    return await getCall('/diagnostics/values');
}

export async function getAllDiagnosticsFarms() {
    return await getCall('/diagnostics/farms');
}

export async function getDimensionIndicators(dimensionId) {
    return await getCall(`/diagnostics/dimensions/${dimensionId}/indicators`);
}

export async function getPrincipleIndicators(principleId) {
    return await getCall(`/diagnostics/principles/${principleId}/indicators`);
}

export async function getDiagnosticsResults(farmIds, indicatorIds, average, startDate, endDate) {
    return await getCall(`/diagnostics/results/${farmIds}/${indicatorIds}/${average}/${startDate}/${endDate}`);
}

export async function getAllSuccessStories(){
    return await getCall('/stories');
}

export function flattenForSheet(input, {pathSep = ".", keepPath = false} = {}) {
    const isScalar = v => v === null || typeof v !== "object" || v instanceof Date;

    const clone = obj => Object.assign({}, obj);

    function expand(value, context, path) {
        // If value is scalar, attach to context and we're done
        if (isScalar(value)) {
            const key = keepPath ? path.join(pathSep) : path[path.length - 1];
            if (key) context[key] = value;
            return [context];
        }

        // If value is an array => explode into multiple rows
        if (Array.isArray(value)) {
            if (value.length === 0) return [context]; // nothing to add, keep parent row
            let result = [];
            for (const [index, element] of value.entries()) {
                // Use index in the path so columns are stable if keepPath=true
                const nextPath = path.length ? [...path.slice(0, -1), `${path[path.length - 1]}[${index}]`] : [String(index)];
                const rowsFromEl = expand(element, clone(context), nextPath);
                result = result.concat(rowsFromEl);
            }
            return result;
        }

        // value is an object: split scalars vs nested (arrays/objects)
        let contextClone = clone(context);
        const nestedEntries = [];
        for (const [k, v] of Object.entries(value)) {
            if (isScalar(v)) {
                const key = keepPath ? [...path, k].join(pathSep) : k;
                contextClone[key] = v;
            } else {
                nestedEntries.push([k, v]);
            }
        }

        // If no nested parts, emit the row with just the scalars
        if (nestedEntries.length === 0) return [contextClone];

        // Otherwise, recursively expand each nested entry and combine results
        let result = [];
        for (const [k, v] of nestedEntries) {
            const rowsFromChild = expand(v, clone(contextClone), [...path, k]);
            result = result.concat(rowsFromChild);
        }
        return result;
    }

    return expand(input, {}, []);
}